/**
 * Sélecteur d'utilisateur pour création de contrat
 * Filtre les utilisateurs externes par société
 * Pré-sélectionne automatiquement si un seul utilisateur disponible
 */

import { useEffect } from 'react';
import { AutocompleteSelect } from '@/Components/shared/AutocompleteSelect';
import { useSocietyUsers } from '@/features/societies/hooks/useSocietyUsers';
import type { User } from '@/features/societies/types/society.types';

interface UserSelectorProps {
  societyId: string | undefined;
  value: string | undefined;
  onChange: (userId: string, user?: { firstname: string; lastname: string }) => void;
  error?: string;
  disabled?: boolean;
  required?: boolean;
  size?: 'default' | 'sm';
  tooltip?: string;
}

export const UserSelector = ({
  societyId,
  value,
  onChange,
  error,
  disabled = false,
  required = false,
  size = 'default',
  tooltip,
}: UserSelectorProps) => {
  const { data: users = [], isLoading } = useSocietyUsers(societyId);

  // Auto-sélection si un seul utilisateur disponible
  useEffect(() => {
    if (!value && users.length === 1) {
      onChange(users[0].uuid, { firstname: users[0].firstName, lastname: users[0].lastName });
    }
  }, [users, value, onChange]);

  const isDisabled = disabled || !societyId;

  // Si pas de societyId, on ne peut pas afficher l'autocomplete
  if (!societyId) {
    return (
      <div>
        <AutocompleteSelect<User>
          apiEndpoint="/societies/no-id/users"
          getOptionLabel={(u) => `${u.firstName} ${u.lastName} (${u.email})`}
          getOptionValue={(u) => u.uuid}
          value={null}
          // eslint-disable-next-line @typescript-eslint/no-empty-function
          onChange={() => {}}
          label="Utilisateur"
          required={required}
          placeholder="Sélectionnez d'abord une société"
          disabled
          size={size}
          tooltip={tooltip}
        />
      </div>
    );
  }

  return (
    <div>
      <AutocompleteSelect<User>
        apiEndpoint={`/societies/${societyId}/users`}
        getOptionLabel={(u) => `${u.firstName} ${u.lastName} (${u.email})`}
        getOptionValue={(u) => u.uuid}
        value={value || null}
        onChange={(uuid, user) => {
          onChange(
            uuid || '',
            user ? { firstname: user.firstName, lastname: user.lastName } : undefined,
          );
        }}
        label="Utilisateur"
        required={required}
        placeholder={isLoading ? 'Chargement...' : 'Sélectionnez un utilisateur'}
        disabled={isDisabled}
        size={size}
        tooltip={tooltip}
        renderOption={(user) => (
          <div>
            <div className="tw-font-medium">{user.firstName} {user.lastName}</div>
            <div className="tw-text-xs tw-text-gray-500">{user.email}</div>
          </div>
        )}
      />
      {error && <p className="tw-mt-1 tw-text-sm tw-text-red-600">{error}</p>}

      {/* Info pré-sélection automatique */}
      {users.length === 1 && value && (
        <p className="tw-text-xs tw-text-blue-600">
          {'\u2139\uFE0F'} Utilisateur unique de la société, sélectionné automatiquement
        </p>
      )}

      {/* Pas d'utilisateurs disponibles */}
      {!isLoading && societyId && users.length === 0 && (
        <p className="tw-text-xs tw-text-orange-600">
          {'\u26A0\uFE0F'} Aucun utilisateur actif dans cette société
        </p>
      )}
    </div>
  );
};
